//@ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import DayPicker, { DateUtils, DayModifiers } from 'react-day-picker';
import { CustomCaption } from './CalendarCustomUI';
import makeStyles from '@mui/styles/makeStyles';
import { classNames } from 'utilities/Utils';
import 'react-day-picker/lib/style.css';
import { INPUT_BOX_SELECTED_STATE } from './RangeCalendarTwoInputs';
import { apiDateFormat, monthYear } from '../../../constants';
import {
  getDates,
  getFormattedDate,
  getFutureDate,
  isDateSame,
  onlyDateComparison,
} from 'utilities/DateUtils';
import Button from '../Button/Button';
import { StyledEngineProvider } from '@mui/material';
type CalendarProps = {
  variant?: 'legacy' | 'latest';
  showTodayButton?: boolean;
  todayBtnText?: 'Today' | 'This Week';
  disabledBefore?: Date;
  disabledAfter?: Date;
  disabledDays?:
  | {
    before: Date;
    after: Date;
  }
  | Date;
  selectedDays?: Date[] | Date;
  month?: Date;
  from?: Date;
  to?: Date;
  enteredTo?: Date;
  enteredFrom?: Date;
  isRangeCalendar?: boolean;
  handleDayMouseEnter?: Function;
  handleYearMonthChange?: Function;
  handleDayClick?: Function;
  inputBoxSelected?: INPUT_BOX_SELECTED_STATE;
  handleFromDayClick?: Function;
  handleToDayClick?: Function;
  showYearChangeIcon?: boolean;
  strikedDates?: Date[];
  hideCalendar?: Function;
  showApplyBtn?: boolean;
  selectedDateMeta?: {
    month?: { [key: string]: number };
    year?: { [key: string]: number };
    count?: number;
  };
  setSelectedDateMeta?: Function;
  dateLabel?: string;
  showDoneBtn?: boolean;
  onDoneClick?: Function;
  doneBtnLabel?: string;
  initialDateOpen?: Date;
  displayDay?: (date: Date, modifiers: DayModifiers) => React.ReactNode;
};
type StyleProps = {
  isCurrentMonthActive: boolean;
  isTodayOrisCurrentWeek: boolean;
  showTodayButton: boolean;
};
const useStyles = makeStyles(theme => {
  return {
    root: ({
      isCurrentMonthActive,
      isTodayOrisCurrentWeek,
      showTodayButton,
    }: StyleProps) => {
      return {
        display: 'flex',
        flexDirection: 'column',
        '& .DayPicker-wrapper': {
          paddingBottom: '8px',
        },
        '& .DayPicker': {
          fontSize: '14px',
          lineHeight: '22px',
        },
        '& .DayPicker-NavBar': {
          display: 'none',
        },
        '& div.DayPicker-Day': {
          width: '28.57px',
          height: '30px',
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '0.02em',
          color: theme.palette.text.primary,
          '&.DayPicker-Day--disabled': {
            color: theme.palette.backgrounds.divider,
          },
        },
        '& div.DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside)':
          {
            backgroundColor: theme.palette.primary.main,
            borderRadius: '4px',
          },
        '& div.DayPicker-Day--today': {
          color: theme.palette.primary.main,
        },
        '& div.DayPicker-Day--today:not(.DayPicker-Day--start):not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside)':
          {
            fontSize: '10px',
            position: 'relative',
            '&:after': {
              content: '""',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              border: `1px solid ${theme.palette.primary.main}`,
              position: 'absolute',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              display: 'inline-block',
            },
          },
        '& .DayPicker-Weekday': {
          fontSize: '10px',
          lineHeight: '14px',
          letterSpacing: '0.02em',
          width: '28.57px',
          height: '18px',
        },
        '& .DayPicker-WeekdaysRow': {
          borderBottom: '1px solid #D2D1D1',
        },
        '& .DayPicker-Footer': {
          marginLeft: '14px',
          borderTop: showTodayButton ? '1px solid #D2D1D1' : 'none',
          marginTop: '6px',
          '& button': {
            fontWeight: 600,
            color:
              isCurrentMonthActive && isTodayOrisCurrentWeek
                ? theme.palette.primary.light
                : theme.palette.primary.main,
            fontSize: '16px',
            padding: '12px',
            cursor:
              isCurrentMonthActive && isTodayOrisCurrentWeek
                ? 'no-drop'
                : 'pointer',
            pointerEvents:
              isCurrentMonthActive && isTodayOrisCurrentWeek ? 'none' : 'auto',
          },
        },
      };
    },
    range: {
      '& div.DayPicker-Day': {
        borderRadius: '0 !important',
        border: 'pointer',
      },
      '& div.DayPicker-Day--start': {
        borderTopLeftRadius: '4px !important',
        borderBottomLeftRadius: '4px !important',
        '&:after': {
          left: '3px',
        },
      },
      '& div.DayPicker-Day--end': {
        borderTopRightRadius: '4px !important',
        borderBottomRightRadius: '4px !important',
        '&:after': {
          right: '3px',
        },
      },
      '& div.DayPicker-Day--start:after,div.DayPicker-Day--end:after:not(.DayPicker-Day--outside)':
        {
          content: '""',
          position: 'absolute',
          border: `0.5px solid ${theme.palette.primary.light}`,
          top: '9px',
          bottom: '9px',
        },
      '& div.DayPicker-Day--start.DayPicker-Day--end:after:not(.DayPicker-Day--outside)':
        {
          content: '""',
          border: 'none',
        },
      '& div.DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside):not(.DayPicker-Day--disabled)':
        {
          color: theme.palette.text.primary,
          background: theme.palette.primary.lighter,
        },
    },
    strike: {
      position: 'relative',
      '&:after': {
        content: '""',
        transform: 'rotate(-45deg)',
        position: 'absolute',
        top: '7px',
        left: 0,
        width: '100%',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        border: `1px solid ${theme.palette.text.secondary}`,
      },
    },
  };
});
const Weekday = props => {
  const { weekday, className, localeUtils, locale } = props;
  let classes = className;
  const weekdayName = localeUtils.formatWeekdayShort(weekday, locale);
  const todaysWeekDayName = localeUtils.formatWeekdayShort(
    new Date().getDay(),
    locale,
  );
  if (weekdayName === todaysWeekDayName) {
    classes += ' text-primary-default';
  } else {
    classes += ' text-[#D2D1D1]';
  }
  return <span className={classes}> {weekdayName.slice(0, 1)}</span>;
};
const Calendar = (props: CalendarProps) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const {
    variant = 'latest',
    selectedDays,
    handleDayClick,
    showTodayButton = false,
    todayBtnText = 'Today',
    disabledBefore = today,
    disabledAfter = new Date(currentYear + 3, 0),
    handleYearMonthChange,
    month,
    // range-calendar-props
    from,
    to,
    enteredTo,
    enteredFrom,
    isRangeCalendar,
    handleDayMouseEnter,
    inputBoxSelected,
    handleFromDayClick,
    handleToDayClick,
    showYearChangeIcon = false,
    hideCalendar,
    strikedDates = [],
    showApplyBtn = true,
    dateLabel = '',
    showDoneBtn = false,
    onDoneClick = () => {},
    doneBtnLabel = 'Done',
    initialDateOpen,
    displayDay,
  } = props;
  const extraProps: Partial<React.ComponentProps<typeof DayPicker>> = { month };
  if (isRangeCalendar) {
    extraProps.onDayMouseEnter = handleDayMouseEnter;
    extraProps.from = from;
    extraProps.to = to;
    extraProps.enteredTo = enteredTo;
    extraProps.enteredFrom = enteredFrom;
    extraProps.modifiers = { start: from, end: to };
    extraProps.selectedDays =
      inputBoxSelected === INPUT_BOX_SELECTED_STATE.TO_INPUT
        ? [from, { from, to }, { from: enteredFrom, to }]
        : [from, { from, to: enteredTo }, { from, to }];
  } else {
    extraProps.selectedDays = selectedDays ?? from;
  }
  if (showTodayButton) {
    extraProps.todayButton = todayBtnText;
    extraProps.onTodayButtonClick = handleDayClick;
  }
  if (variant === 'latest') extraProps.weekdayElement = <Weekday />;

  const strikeDates = useMemo(() => {
    const mapping = {};
    for (const date of strikedDates) {
      mapping[getFormattedDate(date, apiDateFormat)] = true;
    }
    return mapping;
  }, [strikedDates]);
  const isCurrentMonthActive = useMemo(() => {
    if (!month) {
      return true;
    }
    const currentMonth = getFormattedDate(new Date(), monthYear);
    const activeMonth = getFormattedDate(month, monthYear);
    return currentMonth === activeMonth;
  }, [month]);
  const isTodayOrisCurrentWeek = useMemo(() => {
    if (
      todayBtnText === 'Today' &&
      selectedDays &&
      !Array.isArray(selectedDays)
    ) {
      if (isDateSame(new Date(), selectedDays as Date)) {
        return true;
      }
    } else if (todayBtnText === 'This Week') {
      if (
        from &&
        to &&
        isDateSame(new Date(), from) &&
        isDateSame(getFutureDate(new Date(), 7), to)
      ) {
        return true;
      }
    }
    return false;
  }, [from, to]);
  let calendarMonth = today;
  if (isRangeCalendar && from) {
    calendarMonth = from;
  } else {
    const selectedDay = Array.isArray(selectedDays)
      ? selectedDays[0]
      : selectedDays;
    calendarMonth = selectedDay ?? initialDateOpen ?? today;
  }
  if (
    isRangeCalendar &&
    (onlyDateComparison(disabledBefore, calendarMonth) > 0 ||
      onlyDateComparison(disabledAfter, calendarMonth) < 0)
  ) {
    calendarMonth = today;
  }
  const decideHandleDayClick = () => {
    if (inputBoxSelected) {
      if (inputBoxSelected === INPUT_BOX_SELECTED_STATE.FROM_INPUT) {
        return handleFromDayClick;
      }
      return handleToDayClick;
    }
    return handleDayClick;
  };
  const classes = useStyles({
    isCurrentMonthActive,
    isTodayOrisCurrentWeek,
    showTodayButton,
  });
  const renderDay = day => {
    if (displayDay) return displayDay(day);
    const isStrikeDate = strikeDates[getFormattedDate(day, apiDateFormat)];
    return (
      <div className={isStrikeDate && classes.strike}>{day.getDate()}</div>
    );
  };
  return (
    <StyledEngineProvider injectFirst>
      <div
        className={
          variant === 'latest' &&
          classNames(isRangeCalendar && classes.range, classes.root)
        }
      >
        <DayPicker
          selectedDays={selectedDays}
          onDayClick={decideHandleDayClick()}
          captionElement={({ date, localeUtils }) => (
            <CustomCaption
              date={date}
              localeUtils={localeUtils}
              onChange={handleYearMonthChange}
              showYearChangeIcon={showYearChangeIcon}
              dateLabel={dateLabel}
            />
          )}
          disabledDays={[{ after: disabledAfter, before: disabledBefore }]}
          fromMonth={disabledBefore}
          toMonth={disabledAfter}
          initialMonth={calendarMonth}
          renderDay={renderDay}
          {...extraProps}
        />
        {isRangeCalendar && (
          <>
            {!showTodayButton && (
              <div className="border-0 border-t border-[#D2D1D1] border-solid"></div>
            )}
            {showApplyBtn && (
              <div
                className={
                  showTodayButton ? 'absolute bottom-3 right-4' : 'm-3 text-end'
                }
              >
                <Button onClick={() => hideCalendar()}>Apply</Button>
              </div>
            )}
          </>
        )}
        {showDoneBtn && (
          <Button className="done-btn" onClick={() => onDoneClick()} fullWidth>
            {doneBtnLabel}
          </Button>
        )}
      </div>
    </StyledEngineProvider>
  );
};

const useYearMonthState = () => {
  const [month, setMonth] = useState<Date>();
  // TODO: Can have any future changes implemented here like gtm events
  const handleYearMonthChange = function handleChange(monthSelected) {
    setMonth(monthSelected);
  };
  return {
    month,
    handleYearMonthChange,
  };
};
const shouldRestrict = (multiSelectRestriction, selectedDateMeta, day) => {
  const {
    month: monthLimit,
    year: yearLimit,
    total: maxDates,
  } = multiSelectRestriction;
  if (maxDates && maxDates < selectedDateMeta.count) {
    return true;
  }
  const selectedDayMonth = day.getMonth();
  const selectedDayYear = day.getFullYear();
  const yearDateKey = `${selectedDayYear}-${selectedDayMonth}`;
  // No restriction, then no need to compute.
  if (yearLimit === undefined && monthLimit === undefined) {
    return false;
  }
  const { month: monthMap, year: yearCounterMap } = selectedDateMeta;
  // Month check
  if (monthMap?.[yearDateKey] >= monthLimit) {
    return true;
  }
  // Year check
  if (yearCounterMap?.[selectedDayYear] >= yearLimit) {
    return true;
  }
  return false;
};
const useSelectedDateMeta = (selectedDateMeta, setSelectedDateMeta) => {
  const removeDate = day => {
    const monthMap = { ...selectedDateMeta.month };
    const yearMap = { ...selectedDateMeta.year };
    const selectedDayMonth = day.getMonth();
    const selectedDayYear = day.getFullYear();
    const yearDateKey = `${selectedDayYear}-${selectedDayMonth}`;
    monthMap[yearDateKey] -= 1;
    yearMap[selectedDayYear] -= 1;
    return {
      month: monthMap,
      year: yearMap,
      count: selectedDateMeta.count - 1,
    };
  };
  const addDate = day => {
    const monthMap = { ...selectedDateMeta.month };
    const yearMap = { ...selectedDateMeta.year };
    const selectedDayMonth = day.getMonth();
    const selectedDayYear = day.getFullYear();
    const yearDateKey = `${selectedDayYear}-${selectedDayMonth}`;
    if (monthMap[yearDateKey]) monthMap[yearDateKey] += 1;
    else monthMap[yearDateKey] = 1;
    if (yearMap[selectedDayYear]) yearMap[selectedDayYear] += 1;
    else yearMap[selectedDayYear] = 1;
    return {
      month: monthMap,
      year: yearMap,
      count: selectedDateMeta.count + 1,
    };
  };
  const getSelectedDateMeta = () => selectedDateMeta;
  const updatedSelectedDateMeta = (day, toRemove) => {
    const updatedSelectedDate = toRemove ? removeDate(day) : addDate(day);
    setSelectedDateMeta(updatedSelectedDate);
    return updatedSelectedDate;
  };
  return { updatedSelectedDateMeta, getSelectedDateMeta };
};

export type UseCalendarReturnType = {
  selectedDays: Date | Date[];
  setSelectedDays: React.Dispatch<React.SetStateAction<Date | Date[]>>;
  handleDayClick: (
    day: Date,
    {
      disabled,
      selected,
    }: {
      disabled: boolean;
      selected?: boolean;
    },
    event?: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  month: Date;
  handleYearMonthChange: (_month: Date) => void;
  selectedDateMeta: {
    month: {
      [key: string]: number;
    };
    year: {
      [key: string]: number;
    };
    count: number;
  };
  setSelectedDateMeta: Function;
};

export const useCalendar = (
  initialDate: Date | Date[],
  acceptMultipleDates = false,
  callbackAfterDateSelection = () => {},
  multiSelectRestriction: {
    month?: number;
    year?: number;
    total?: number;
  } = {},
  selectedDateMeta: {
    month: { [key: string]: number };
    year: { [key: string]: number };
    count: number;
  } = {
    month: {},
    year: {},
    count: 0,
  },
  setSelectedDateMeta: Function = () => {},
  defaultNull = false,
): UseCalendarReturnType => {
  const [selectedDays, setSelectedDays] = useState<Date | Date[]>(() => {
    if (acceptMultipleDates) {
      return initialDate ? initialDate : [];
    }
    if (defaultNull && !initialDate) {
      return null;
    }
    return initialDate ?? new Date();
  });
  useEffect(() => {
    callbackAfterDateSelection(selectedDays);
  }, [selectedDays]);
  const { updatedSelectedDateMeta, getSelectedDateMeta } = useSelectedDateMeta(
    selectedDateMeta,
    setSelectedDateMeta,
  );
  const { month, handleYearMonthChange } = useYearMonthState();
  const handleDayClick = (
    day: Date,
    { disabled, selected = false }: { disabled: boolean; selected?: boolean },
    event: React.MouseEvent<HTMLDivElement, MouseEvent> = null,
  ) => {
    let currentSelectedDays = null;
    if (disabled) {
      return;
    }
    /*
      Today button text can be "Today" OR "ThisWeek"
      Disable the "Today" OR "ThisWeek" button
      Update the selected days on clcik of ThisWeek Button
    */
    if (event) {
      if (event.nativeEvent.target.className === 'DayPicker-TodayButton') {
        handleYearMonthChange(new Date());
        if (
          acceptMultipleDates &&
          event.nativeEvent.target.innerText === 'This Week'
        ) {
          const curr = new Date();
          const first = curr.getDate() - curr.getDay();
          const firstDay = new Date(curr.setDate(first));
          const dates = getDates(7, firstDay).map(date => new Date(date));
          setSelectedDays(dates);
          return;
        }
      } else {
        handleYearMonthChange(day);
      }
    }
    if (!acceptMultipleDates) {
      currentSelectedDays = day;
    } else {
      currentSelectedDays = [...selectedDays];
      const _selectedDateMeta = getSelectedDateMeta();
      if (
        !selected &&
        shouldRestrict(multiSelectRestriction, _selectedDateMeta, day)
      ) {
        return;
      }
      updatedSelectedDateMeta(day, selected);
      if (selected) {
        const selectedIndex = currentSelectedDays.findIndex(selectedDay =>
          DateUtils.isSameDay(selectedDay, day),
        );
        currentSelectedDays.splice(selectedIndex, 1);
      } else {
        currentSelectedDays.push(day);
      }
    }
    setSelectedDays(currentSelectedDays);
    /*
    Removing this call to avoid unnecessary re-render since the function is
    called whenever the currentSelectedDays change (useEffect hook)
    */
    // callback handle not needed as done btn is present to close the calendar for multi dates
    // if (acceptMultipleDates) {
    //   return;
    // }
    // callbackAfterDateSelection(currentSelectedDays);
  };
  return {
    selectedDays,
    setSelectedDays,
    handleDayClick,
    month,
    handleYearMonthChange,
    selectedDateMeta,
    setSelectedDateMeta,
  };
};

function isSelectingFirstDay(from, to, day) {
  const isRangeSelected = from && to;
  const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
  return !from || isBeforeFirstDay || isRangeSelected;
}

export type UseRangeCalendarReturnType = {
  from: Date;
  to: Date;
  isFirstClickInToInput: boolean;
  isFirstClickInFromInput: boolean;
  enteredTo: Date | null;
  enteredFrom: Date | null;
  disabledBefore: Date | null;
  disabledAfter: Date | null;
  handleDayClick: (day: Date) => void;
  handleDayMouseEnter: (day: Date) => void;
  month: Date;
  handleYearMonthChange: (month: Date) => void;
  setRange: (from: Date, to: Date) => void;
  handleFromDayClick: (day: Date) => void;
  handleToDayClick: (day: Date) => void;
  setIsFirstClickInFromInput: (isFirstClick: boolean) => void;
  setIsFirstClickInToInput: (isFirstClick: boolean) => void;
};

export function useRangeCalendar(
  initialState = [null, null],
  callback: Function = () => {},
  disabledDays: { disabledBefore?: Date; disabledAfter?: Date } = {},
  variant = 'latest',
): UseRangeCalendarReturnType {
  const [from, setFrom] = useState(initialState[0]);
  const [to, setTo] = useState(initialState[1]);
  const [enteredFrom, setEnteredFrom] = useState(initialState[0]);
  const [enteredTo, setEnteredTo] = useState(initialState[1]); // Keep track of the last day for mouseEnter.
  const { month, handleYearMonthChange } = useYearMonthState();
  const [disabledBefore, setDisabledBefore] = useState<Date>(
    disabledDays?.disabledBefore,
  ); // to disable before dates after from is selected
  const [disabledAfter, setDisabledAfter] = useState<Date>(
    disabledDays?.disabledAfter,
  ); // to disable after dates after to is selected
  //This state will be true when it is the first click after opening the Calendar
  const [isFirstClickInFromInput, setIsFirstClickInFromInput] = useState(true);
  const [isFirstClickInToInput, setIsFirstClickInToInput] = useState(true);
  function handleReset() {
    setFrom(null);
    setTo(null);
    setEnteredTo(null);
    callback({ from: null, to: null });
  }
  function handleDayClick(day, { disabled }, event) {
    if (disabled) {
      return;
    }
    /*
      Today button text can be "Today" OR "ThisWeek"
      Disable the "Today" OR "ThisWeek" button
      Update the from, to, enteredTo, enteredFrom variable on clcik of ThisWeek Button
    */
    if (event.nativeEvent.target.className === 'DayPicker-TodayButton') {
      handleYearMonthChange(new Date());
      if (event.nativeEvent.target.innerText === 'This Week') {
        const curr = new Date();
        const first = curr.getDate() - curr.getDay();
        const last = first + 6;
        const firstDay = new Date(curr.setDate(first));
        const lastDay = new Date(curr.setDate(last));
        setDisabledBefore(firstDay);
        setFrom(firstDay);
        setTo(lastDay);
        setEnteredFrom(firstDay);
        setEnteredTo(lastDay);
        return;
      }
    }
    if (from && to && day >= from && day < to) {
      handleReset();
      return;
    }
    if (isSelectingFirstDay(from, to, day)) {
      setFrom(day);
      setTo(null);
      setEnteredTo(null);
      if (variant === 'legacy') callback({ from: day, to: null });
    } else {
      setTo(day);
      setEnteredTo(day);
      callback({ from, to: day });
    }
  }
  const handleFromDayClick = (day, { disabled }) => {
    if (disabled) {
      return;
    }
    if (isFirstClickInFromInput) {
      if (to && DateUtils.isDayAfter(day, to)) {
        setIsFirstClickInFromInput(false);
        setFrom(day);
        setEnteredFrom(null);
        setTo(null);
        setDisabledBefore(day);
        setDisabledAfter(disabledDays?.disabledAfter);
        callback({ from: day, to: null });
      } else {
        setIsFirstClickInFromInput(false);
        setFrom(day);
        setDisabledBefore(day);
        setDisabledAfter(disabledDays?.disabledAfter);
        // setEnteredTo(null);
        setEnteredFrom(null);
        callback({ from: day, to });
      }
    } else {
      setIsFirstClickInFromInput(true);
      setTo(day);
      setEnteredTo(day);
      setDisabledBefore(disabledDays?.disabledBefore ?? new Date());
      setDisabledAfter(disabledDays?.disabledAfter);
      callback({ from, to: day });
    }
  };
  const handleToDayClick = (day, { disabled }) => {
    if (disabled) {
      return;
    }
    if (isFirstClickInToInput) {
      if (from && DateUtils.isDayBefore(day, from)) {
        setIsFirstClickInToInput(false);
        setTo(day);
        setEnteredTo(null);
        setFrom(null);
        setDisabledAfter(day);
        setDisabledBefore(disabledDays?.disabledBefore ?? new Date());
        callback({ from: null, to: day });
      } else {
        setIsFirstClickInToInput(false);
        setTo(day);
        setDisabledAfter(day);
        setDisabledBefore(disabledDays?.disabledBefore ?? new Date());
        // setEnteredTo(null);
        setEnteredTo(null);
        callback({ from, to: day });
      }
    } else {
      setIsFirstClickInToInput(true);
      setFrom(day);
      setEnteredFrom(day);
      setDisabledAfter(disabledDays?.disabledAfter);
      setDisabledBefore(disabledDays?.disabledBefore ?? new Date());
      callback({ from: day, to });
    }
  };
  function handleDayMouseEnter(day) {
    if (from && !isFirstClickInFromInput) {
      setEnteredTo(day);
    }
    if (to && !isFirstClickInToInput) {
      setEnteredFrom(day);
    }
  }
  function setRange(fromValue, toValue) {
    setFrom(fromValue);
    setTo(toValue);
    setEnteredTo(toValue);
    setEnteredFrom(fromValue);
  }
  return {
    from,
    to,
    isFirstClickInToInput,
    isFirstClickInFromInput,
    enteredTo,
    enteredFrom,
    disabledBefore,
    disabledAfter,
    handleDayClick,
    handleDayMouseEnter,
    month,
    handleYearMonthChange,
    setRange,
    handleFromDayClick,
    handleToDayClick,
    setIsFirstClickInFromInput,
    setIsFirstClickInToInput,
  };
}
export default Calendar;
